import React, { useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  Progress,
  Spinner,
  Pagination,
  Select,
  SelectItem,
} from '@heroui/react'

import { useToast } from '../../../hooks'
import {
  getGifLoopDetails,
  batchRepairGifLoops,
  formatFileSize,
  formatUploadDate,
  type BatchLoopRepairResult,
} from '../services/mediaManagementService'

// Simple icon components
const CheckIcon = () => <span>✅</span>
const LoopIcon = () => <span>🔁</span>
const ScanIcon = () => <span>🔍</span>

// Types
interface LoopScanState {
  isScanning: boolean
  scannedCount: number
  totalCount: number
  foundIssues: number
  canCancel: boolean
}

interface LoopRepairProgress {
  isRunning: boolean
  currentBatch: string[]
  results: BatchLoopRepairResult | null
  error: string | null
}

interface LoopData {
  items: Array<{
    gifId: string
    fileName: string
    fileSizeBytes: number
    uploadedAt: string
    loopInfo: {
      hasLoopExtension: boolean
      isGif89a: boolean
      frameCount: number
      needsRepair: boolean
    }
  }>
  totalCount: number
  hasMore: boolean
  nextOffset: number
}

interface LoopAnalysisProps {
  onShowProgress: (type: 'repair', progress: LoopRepairProgress) => void
}

export const LoopAnalysis: React.FC<LoopAnalysisProps> = ({
  onShowProgress,
}) => {
  const [loopData, setLoopData] = useState<LoopData | null>(null)
  const [loopScanState, setLoopScanState] = useState<LoopScanState>({
    isScanning: false,
    scannedCount: 0,
    totalCount: 0,
    foundIssues: 0,
    canCancel: false,
  })
  const [loopPage, setLoopPage] = useState(1)
  const [loopPageSize, setLoopPageSize] = useState(25)
  const [loopSearchTerm, setLoopSearchTerm] = useState('')
  const [selectedLoopGifs, setSelectedLoopGifs] = useState<Set<string>>(
    new Set()
  )
  const [loopRepairProgress, setLoopRepairProgress] =
    useState<LoopRepairProgress>({
      isRunning: false,
      currentBatch: [],
      results: null,
      error: null,
    })

  const toast = useToast()

  // Loop Management Functions
  const scanForLoopIssues = async () => {
    setLoopScanState({
      isScanning: true,
      scannedCount: 0,
      totalCount: 0,
      foundIssues: 0,
      canCancel: true,
    })

    try {
      let allLoopIssues: Array<{
        gifId: string
        fileName: string
        fileSizeBytes: number
        uploadedAt: string
        loopInfo: {
          hasLoopExtension: boolean
          isGif89a: boolean
          frameCount: number
          needsRepair: boolean
        }
      }> = []
      let offset = 0
      let hasMore = true
      let totalAnalyzed = 0

      while (hasMore) {
        const loopDetails = await getGifLoopDetails({ offset, limit: 50 })

        allLoopIssues = [...allLoopIssues, ...loopDetails.loopIssuesList]
        totalAnalyzed += loopDetails.totalAnalyzed
        hasMore = loopDetails.hasMore
        offset = loopDetails.nextOffset

        setLoopScanState((prev) => ({
          ...prev,
          scannedCount: totalAnalyzed,
          foundIssues: allLoopIssues.length,
        }))

        if (hasMore) {
          toast.success(
            `Analyzed ${totalAnalyzed} GIFs, found ${allLoopIssues.length} with issues...`,
            { duration: 1000 }
          )
        }
      }

      setLoopData({
        items: allLoopIssues,
        totalCount: allLoopIssues.length,
        hasMore: false,
        nextOffset: 0,
      })

      setLoopPage(1) // Reset to first page
      setSelectedLoopGifs(new Set()) // Clear selections

      toast.success(
        `Analysis complete! Found ${allLoopIssues.length} GIFs with loop issues out of ${totalAnalyzed} analyzed`
      )
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to scan for loop issues'
      toast.error(errorMessage)
    } finally {
      setLoopScanState((prev) => ({
        ...prev,
        isScanning: false,
        canCancel: false,
      }))
    }
  }

  // Selection handlers
  const handleSelectAllLoop = (checked: boolean) => {
    const currentPageItems = getPaginatedLoopItems()
    if (checked) {
      const newSelected = new Set(selectedLoopGifs)
      currentPageItems.forEach((gif) => newSelected.add(gif.gifId))
      setSelectedLoopGifs(newSelected)
    } else {
      const newSelected = new Set(selectedLoopGifs)
      currentPageItems.forEach((gif) => newSelected.delete(gif.gifId))
      setSelectedLoopGifs(newSelected)
    }
  }

  const handleSelectLoopGif = (gifId: string, checked: boolean) => {
    const newSelected = new Set(selectedLoopGifs)
    if (checked) {
      newSelected.add(gifId)
    } else {
      newSelected.delete(gifId)
    }
    setSelectedLoopGifs(newSelected)
  }

  // Pagination helpers
  const getFilteredLoopItems = () => {
    if (!loopData) return []

    return loopData.items.filter(
      (gif) =>
        gif.gifId.toLowerCase().includes(loopSearchTerm.toLowerCase()) ||
        gif.fileName.toLowerCase().includes(loopSearchTerm.toLowerCase())
    )
  }

  const getPaginatedLoopItems = () => {
    const filtered = getFilteredLoopItems()
    const startIndex = (loopPage - 1) * loopPageSize
    const endIndex = startIndex + loopPageSize
    return filtered.slice(startIndex, endIndex)
  }

  const getLoopTotalPages = () => {
    const filtered = getFilteredLoopItems()
    return Math.ceil(filtered.length / loopPageSize)
  }

  // Batch operations
  const handleBatchRepair = async () => {
    if (selectedLoopGifs.size === 0) return

    const gifIds = Array.from(selectedLoopGifs)
    const progress: LoopRepairProgress = {
      isRunning: true,
      currentBatch: gifIds,
      results: null,
      error: null,
    }

    setLoopRepairProgress(progress)
    onShowProgress('repair', progress)

    try {
      const result = await batchRepairGifLoops(gifIds)
      const updatedProgress = {
        ...progress,
        isRunning: false,
        results: result,
      }
      setLoopRepairProgress(updatedProgress)
      onShowProgress('repair', updatedProgress)

      // Remove successfully repaired items from the list
      const successfulIds = result.results
        .filter((r) => r.success)
        .map((r) => r.gifId)
      if (loopData) {
        setLoopData({
          ...loopData,
          items: loopData.items.filter(
            (item) => !successfulIds.includes(item.gifId)
          ),
          totalCount: loopData.totalCount - successfulIds.length,
        })
      }

      // Clear selections
      setSelectedLoopGifs(new Set())

      toast.success(
        `Successfully repaired ${result.successful} GIF loop extensions!`
      )
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Loop repair failed'
      const updatedProgress = {
        ...progress,
        isRunning: false,
        error: errorMessage,
      }
      setLoopRepairProgress(updatedProgress)
      onShowProgress('repair', updatedProgress)
      toast.error(errorMessage)
    }
  }

  // Calculate current page selection states
  const currentLoopItems = getPaginatedLoopItems()
  const allLoopSelected =
    currentLoopItems.length > 0 &&
    currentLoopItems.every((gif) => selectedLoopGifs.has(gif.gifId))
  const someLoopSelected = currentLoopItems.some((gif) =>
    selectedLoopGifs.has(gif.gifId)
  )

  return (
    <div className="space-y-4">
      {/* Loop Overview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <div>
              <h3 className="text-lg font-semibold">Loop Extensions</h3>
              <p className="text-slate-600 dark:text-slate-400">
                Analyze GIFs for proper loop extensions to ensure autoplay
                compatibility
              </p>
            </div>
            <Button
              onPress={scanForLoopIssues}
              isDisabled={loopScanState.isScanning}
              color="primary"
              variant="flat"
              startContent={
                loopScanState.isScanning ? <Spinner size="sm" /> : <ScanIcon />
              }
            >
              {loopScanState.isScanning
                ? 'Analyzing...'
                : 'Analyze Loop Extensions'}
            </Button>
          </div>
        </CardHeader>

        {loopScanState.isScanning && (
          <CardBody>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">
                  Analyzed {loopScanState.scannedCount} GIFs
                </span>
                <span className="text-sm font-medium">
                  Found {loopScanState.foundIssues} with issues
                </span>
              </div>
              <Progress isIndeterminate className="w-full" color="primary" />
            </div>
          </CardBody>
        )}
      </Card>

      {/* Loop Results Table */}
      {loopData && loopData.totalCount > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between w-full">
              <div>
                <h3 className="text-lg font-semibold">
                  GIFs with Loop Issues ({loopData.totalCount})
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Select GIFs to repair loop extensions
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Chip variant="bordered" size="sm">
                  {selectedLoopGifs.size} selected
                </Chip>
                <Button
                  onPress={handleBatchRepair}
                  isDisabled={
                    selectedLoopGifs.size === 0 || loopRepairProgress.isRunning
                  }
                  color="primary"
                  startContent={<LoopIcon />}
                >
                  Repair Selected
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            {/* Search and Page Size Controls */}
            <div className="flex items-center justify-between">
              <Input
                placeholder="Search by GIF ID or filename..."
                value={loopSearchTerm}
                onValueChange={setLoopSearchTerm}
                className="max-w-sm"
              />
              <Select
                label="Per page"
                value={loopPageSize.toString()}
                onChange={(e) => {
                  setLoopPageSize(Number(e.target.value))
                  setLoopPage(1) // Reset to first page
                }}
                className="w-32"
                size="sm"
              >
                <SelectItem key="25">25</SelectItem>
                <SelectItem key="50">50</SelectItem>
                <SelectItem key="100">100</SelectItem>
              </Select>
            </div>

            {/* Table */}
            <Table>
              <TableHeader>
                <TableColumn className="w-12">
                  <Checkbox
                    isSelected={allLoopSelected}
                    onValueChange={handleSelectAllLoop}
                    isIndeterminate={someLoopSelected && !allLoopSelected}
                  />
                </TableColumn>
                <TableColumn>GIF ID</TableColumn>
                <TableColumn>Filename</TableColumn>
                <TableColumn>Size</TableColumn>
                <TableColumn>Frames</TableColumn>
                <TableColumn>Status</TableColumn>
                <TableColumn>Uploaded</TableColumn>
              </TableHeader>
              <TableBody>
                {currentLoopItems.map((gif) => (
                  <TableRow key={gif.gifId}>
                    <TableCell>
                      <Checkbox
                        isSelected={selectedLoopGifs.has(gif.gifId)}
                        onValueChange={(checked) =>
                          handleSelectLoopGif(gif.gifId, checked)
                        }
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {gif.gifId}
                    </TableCell>
                    <TableCell>{gif.fileName}</TableCell>
                    <TableCell>{formatFileSize(gif.fileSizeBytes)}</TableCell>
                    <TableCell>{gif.loopInfo.frameCount}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {!gif.loopInfo.hasLoopExtension && (
                          <Chip size="sm" color="warning" variant="flat">
                            No Loop Extension
                          </Chip>
                        )}
                        {!gif.loopInfo.isGif89a && (
                          <Chip size="sm" color="secondary" variant="flat">
                            GIF87a Format
                          </Chip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatUploadDate(gif.uploadedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {getLoopTotalPages() > 1 && (
              <div className="flex justify-center">
                <Pagination
                  total={getLoopTotalPages()}
                  page={loopPage}
                  onChange={setLoopPage}
                  showControls
                  showShadow
                />
              </div>
            )}

            {getFilteredLoopItems().length === 0 && loopSearchTerm && (
              <div className="text-center py-4 text-slate-600">
                No GIFs found matching &quot;{loopSearchTerm}&quot;
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Loop Success State */}
      {loopData && loopData.totalCount === 0 && !loopScanState.isScanning && (
        <Card>
          <CardBody className="pt-6">
            <div className="text-center">
              <div className="text-6xl mb-4">
                <CheckIcon />
              </div>
              <h3 className="text-lg font-medium mb-2">
                All GIFs have proper loop extensions!
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Every analyzed GIF has proper loop extensions for optimal
                autoplay compatibility.
              </p>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default LoopAnalysis
